import { useCallback, useEffect, useState } from 'react';
import type { Api, SuServerStatus } from '../../api';
import { fmtDateTime, NONE } from '../../admin/format';
import { fmtBytes, fmtUptime } from './labels';

/**
 * 서버 상태(수퍼유저, §14.19.3 ③) — **보기 전용**. 조작(재시작·설정)은 후속.
 * 조각 하나(디스크·DB)를 못 읽어도 나머지는 보인다(서버가 그 칸만 비운다).
 */
export function SuServerView({ api }: { api: Api }) {
  const [s, setS] = useState<SuServerStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setS(await api.suServer());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 표시할 줄 — 데이터로 정의(칸을 늘릴 때 JSX 복사 없이). */
  const rows: Array<[string, string]> = s
    ? [
        ['서버 시각', fmtDateTime(s.serverTime)],
        ['가동 시간', `${fmtUptime(s.uptimeSec)} (시작 ${fmtDateTime(s.startedAt)})`],
        ['빌드 커밋', s.buildCommit ?? NONE],
        ['Node', s.node],
        ['운영체제', s.platform],
        ['메모리(서버 프로세스)', `${fmtBytes(s.memory.rssBytes)} (힙 ${fmtBytes(s.memory.heapUsedBytes)})`],
        ['메모리(장비)', `사용 가능 ${fmtBytes(s.memory.freeBytes)} / 전체 ${fmtBytes(s.memory.totalBytes)}`],
        ['부하(1·5·15분)', s.loadAvg.join(' · ')],
        ['디스크', s.disk ? `여유 ${fmtBytes(s.disk.freeBytes)} / 전체 ${fmtBytes(s.disk.totalBytes)} (${s.disk.path})` : NONE],
        ['DB 이행', `${s.db.migrations}건 · 최근 ${s.db.lastMigration ?? NONE} (${fmtDateTime(s.db.lastMigratedAt)})`],
        ['DB 크기', fmtBytes(s.db.sizeBytes)],
        ['접속 중', `기기 ${s.online.devices} · 뷰어 ${s.online.viewers}`],
        ['게시된 앱', s.release.version ? `${s.release.version} (${fmtDateTime(s.release.publishedAt)})` : NONE],
      ]
    : [];

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>서버</h2>
        <button className="ghost" onClick={() => void refresh()} disabled={loading}>
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </div>
      {error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{error}</div>}
      {s && (
        <dl className="su-kv">
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
